import React, { useEffect, useState, useMemo } from 'react';
import { departmentApi } from '@/features/workspace/api';
import { DownloadFileIcon } from './DepartmentIcons';

export const DepartmentAnalyticsView: React.FC = () => {
  const [students, setStudents] = useState<any[]>([]);
  const [, setLoading] = useState<boolean>(true);
  const [activeTemplate, setActiveTemplate] = useState<string>('all');

  useEffect(() => {
    departmentApi
      .monitoring()
      .then((res) => setStudents(res?.students || []))
      .catch(() => setStudents([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredStudents = useMemo(() => {
    if (activeTemplate === 'course_1') return students.filter((s) => s.course === 1);
    if (activeTemplate === 'working') return students.filter((s) => s.has_job);
    if (activeTemplate === 'under_30') return students.filter((s) => s.age <= 30);
    if (activeTemplate === 'nird_risk')
      return students.filter((s) => s.nird_status === 'Не сдан' || s.nird_status === 'Черновик');
    return students;
  }, [students, activeTemplate]);

  const totalInSelection = filteredStudents.length;
  const avgAge =
    totalInSelection > 0
      ? Math.round(filteredStudents.reduce((acc, s) => acc + s.age, 0) / totalInSelection)
      : 29;
  const workingCount = filteredStudents.filter((s) => s.has_job).length;
  const avgProgress =
    totalInSelection > 0
      ? Math.round(filteredStudents.reduce((acc, s) => acc + s.progress, 0) / totalInSelection)
      : 74;

  const handleExportCsv = () => {
    if (!filteredStudents.length) return;
    const headers = [
      '#',
      'Докторант',
      'Шифр',
      'Возраст',
      'Курс',
      'Программа',
      'Место работы',
      'НИРД',
      'Прогресс %',
      'Научный руководитель',
      'Статус защиты',
      'Дата защиты',
    ];
    const rows = filteredStudents.map((s, idx) => [
      idx + 1,
      `"${s.full_name}"`,
      `"${s.student_code}"`,
      s.age,
      s.course,
      `"${s.program_code} · ${s.program_title}"`,
      `"${s.workplace}"`,
      `"${s.nird_status}"`,
      s.progress,
      `"${s.supervisor_name}"`,
      `"${s.defense_status}"`,
      `"${s.defense_date || ''}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `department_report_${activeTemplate}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-6">
        <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
          Reporting
        </span>
        <h2 className="text-2xl font-bold text-white tracking-tight mt-1">
          Отчётность и служебные сводки
        </h2>
        <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-4xl">
          Формируйте готовые списки по данным кафедры и сохраняйте результат как рабочую сводку.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">В выборке</span>
            <div className="text-3xl font-extrabold text-white mt-1">{totalInSelection}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Средний возраст</span>
            <div className="text-3xl font-extrabold text-cyan-400 mt-1">{avgAge}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Работают</span>
            <div className="text-3xl font-extrabold text-emerald-400 mt-1">{workingCount}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Средний прогресс</span>
            <div className="text-3xl font-extrabold text-white mt-1">{avgProgress}%</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-white">Шаблоны отчётов</h3>
            <p className="text-xs text-slate-400 mt-0.5">Выберите тип — ниже сразу строится фактическая таблица.</p>
          </div>
          <button
            type="button"
            onClick={handleExportCsv}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs border border-slate-700 transition-all shadow active:scale-95"
          >
            <DownloadFileIcon className="w-4 h-4 text-cyan-400" />
            <span>Экспорт CSV</span>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-800">
          {[
            { id: 'all', label: 'Все докторанты' },
            { id: 'course_1', label: '1 курс' },
            { id: 'working', label: 'Места работы' },
            { id: 'under_30', label: 'До 30 лет' },
            { id: 'nird_risk', label: 'НИРД: риск' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTemplate(tab.id)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTemplate === tab.id
                  ? 'bg-cyan-500 text-slate-950 shadow-md font-extrabold'
                  : 'bg-[#061420] text-slate-300 hover:text-white border border-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-slate-800 bg-[#061420] text-slate-400 uppercase text-[10px] font-bold">
                <th className="py-3 px-3 w-10 text-center">#</th>
                <th className="py-3 px-4">Докторант</th>
                <th className="py-3 px-3 text-center">Курс</th>
                <th className="py-3 px-4">Программа</th>
                <th className="py-3 px-4">Место работы</th>
                <th className="py-3 px-3 text-center">НИРД</th>
                <th className="py-3 px-4">Прогресс</th>
                <th className="py-3 px-4">Научный руководитель</th>
                <th className="py-3 px-4 text-center">Защита</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredStudents.map((s, idx) => (
                <tr key={s.id} className="hover:bg-[#0c2336] transition-colors">
                  <td className="py-3 px-3 text-center font-mono text-slate-500">{idx + 1}</td>
                  <td className="py-3 px-4">
                    <div className="font-bold text-white">{s.full_name}</div>
                    <div className="text-[10px] font-mono text-slate-400">
                      {s.student_code} · {s.age} лет
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center font-bold text-cyan-400">{s.course}</td>
                  <td className="py-3 px-4 text-slate-300">
                    <div className="font-medium">{s.program_code}</div>
                    <div className="text-[10px] text-slate-500">{s.program_title}</div>
                  </td>
                  <td className="py-3 px-4 text-slate-300">{s.workplace}</td>
                  <td className="py-3 px-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                      s.nird_status === 'Сдан'
                        ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                        : s.nird_status === 'На проверке'
                        ? 'bg-cyan-950 text-cyan-300 border border-cyan-800'
                        : s.nird_status === 'Черновик'
                        ? 'bg-amber-950 text-amber-300 border border-amber-800'
                        : 'bg-rose-950 text-rose-300 border border-rose-800'
                    }`}>
                      {s.nird_status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="font-bold font-mono text-white text-[11px] w-8">
                        {s.progress}%
                      </span>
                      <div className="w-20 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className="h-full bg-cyan-400 rounded-full"
                          style={{ width: `${s.progress}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-300 font-medium">{s.supervisor_name}</td>
                  <td className="py-3 px-4 text-center">
                    {s.defense_date ? (
                      <div className="inline-block px-2.5 py-1 rounded-lg bg-emerald-950/80 border border-emerald-600/40 text-emerald-300 text-left">
                        <div className="font-bold text-[10px]">Защищён</div>
                        <div className="text-[9px] font-mono text-emerald-400">{s.defense_date}</div>
                      </div>
                    ) : (
                      <div className="inline-block px-2 py-1 rounded-lg bg-slate-800/60 border border-slate-700/60 text-slate-400 text-[10px]">
                        <div>Без зафиксированной защиты</div>
                        <div className="text-center font-mono">—</div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
